import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '@/components/Layout';
import { Card, CardContent } from '@/components/ui/card';
import { BarChart3, Users, BookOpen, CheckCircle } from 'lucide-react';

import { BACKEND_URL } from '../../config/api';
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('lms_token')}`
});

export default function TeacherReports({ user, onLogout }) {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchReport();
  }, []);

  const fetchReport = async () => {
    try {
      const res = await axios.get(`${API}/teacher/reports`, { headers: getAuthHeaders() });
      setReport(res.data);
    } catch (error) {
      console.error('Failed to fetch report:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <Layout user={user} onLogout={onLogout} currentPage="teacher-reports"><div className="p-8">Loading...</div></Layout>;
  }

  if (!report) {
    return <Layout user={user} onLogout={onLogout} currentPage="teacher-reports"><div className="p-8">No report data available</div></Layout>;
  }

  return (
    <Layout user={user} onLogout={onLogout} currentPage="teacher-reports">
      <div className="p-6 lg:p-8 space-y-6">
        <div>
          <h1 className="text-3xl lg:text-4xl font-bold text-navy mb-2">📊 Teaching Reports</h1>
          <p className="text-[#4D4D4D] font-light">Overview of your teaching activities</p>
        </div>

        {/* Overall Statistics */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="border-0 card-shadow bg-white rounded-2xl card-hover transition-smooth">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-[#4D4D4D]">Total Courses</p>
                  <p className="text-3xl font-bold mt-2 text-primary-blue">{report.total_courses}</p>
                </div>
                <div className="w-12 h-12 rounded-full flex items-center justify-center bg-primary-blue/10">
                  <BookOpen className="w-6 h-6 text-primary-blue" strokeWidth={2} />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 card-shadow bg-white rounded-2xl card-hover transition-smooth">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-[#4D4D4D]">Total Students</p>
                  <p className="text-3xl font-bold mt-2 text-primary-blue">{report.total_students}</p>
                </div>
                <div className="w-12 h-12 rounded-full flex items-center justify-center bg-primary-blue/10">
                  <Users className="w-6 h-6 text-primary-blue" strokeWidth={2} />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 card-shadow bg-white rounded-2xl card-hover transition-smooth">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-[#4D4D4D]">Total Lessons</p>
                  <p className="text-3xl font-bold mt-2 text-primary-blue">{report.total_lessons}</p>
                </div>
                <div className="w-12 h-12 rounded-full flex items-center justify-center bg-primary-blue/10">
                  <CheckCircle className="w-6 h-6 text-primary-blue" strokeWidth={2} />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 card-shadow bg-white rounded-2xl card-hover transition-smooth">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-[#4D4D4D]">Quizzes Taken</p>
                  <p className="text-3xl font-bold mt-2 text-primary-blue">{report.total_quizzes_taken}</p>
                </div>
                <div className="w-12 h-12 rounded-full flex items-center justify-center bg-primary-blue/10">
                  <BarChart3 className="w-6 h-6 text-primary-blue" strokeWidth={2} />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Course-wise Statistics */}
        <div className="space-y-4">
          <h2 className="text-xl font-bold text-navy">📊 Course-wise Statistics</h2>
          {report.course_stats.length === 0 ? (
            <Card className="border-0 card-shadow bg-white rounded-2xl">
              <CardContent className="p-12 text-center">
                <BookOpen className="w-20 h-20 mx-auto mb-4 text-[#E7E7E7]" strokeWidth={1.5} />
                <p className="text-xl font-bold text-navy">No courses created yet</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {report.course_stats.map((course) => (
                <Card key={course.course_id} className="border-0 card-shadow bg-white rounded-2xl">
                  <CardContent className="p-6">
                    <h3 className="text-lg font-bold text-navy mb-4">{course.course_title}</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="text-center p-3 rounded-xl bg-blue-50">
                        <p className="text-2xl font-bold text-primary-blue">{course.students_enrolled}</p>
                        <p className="text-xs font-medium text-[#4D4D4D]">Students</p>
                      </div>
                      <div className="text-center p-3 rounded-xl bg-blue-50">
                        <p className="text-2xl font-bold text-primary-blue">{course.lessons_count}</p>
                        <p className="text-xs font-medium text-[#4D4D4D]">Lessons</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}